import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Tag, Typography, Space, Button, Tooltip,
  message, Spin, Empty, Badge
} from 'antd';
import {
  QrcodeOutlined, BankOutlined, WalletOutlined, AppstoreOutlined,
  CopyOutlined, ArrowUpOutlined, ArrowDownOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;

const merchantApi = axios.create({ baseURL: '/api/merchant' });
merchantApi.interceptors.request.use(config => {
  const token = localStorage.getItem('merchant_access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const formatFee = (product) => {
  const fee = parseFloat(product.fee_merchant);
  if (product.fee_type === 'percentage') {
    return `${(fee * 100).toFixed(2).replace(/\.?0+$/, '')}%`;
  } else if (product.fee_type === 'flat') {
    return `Rp ${fee.toLocaleString('id-ID')}`;
  }
  return `${(fee * 100).toFixed(2)}% + Rp ${parseFloat(product.min_fee || 0).toLocaleString('id-ID')}`;
};

const categoryIcon = (code, flowType) => {
  const style = { fontSize: 22, color: flowType === 'cash_in' ? '#0f3460' : '#cf1322' };
  switch (code) {
    case 'QRIS':     return <QrcodeOutlined style={style} />;
    case 'VA':       return <BankOutlined style={style} />;
    case 'TRANSFER': return <BankOutlined style={style} />;
    case 'TOPUP':    return <WalletOutlined style={style} />;
    default:         return <AppstoreOutlined style={style} />;
  }
};

const MerchantProducts = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await merchantApi.get('/products');
      setCategories(res.data?.categories || []);
    } catch {
      message.error('Gagal memuat daftar produk');
    } finally {
      setLoading(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    message.success(`"${text}" disalin`);
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" /></div>;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3}><AppstoreOutlined /> Produk Aktif</Title>
        <Text type="secondary">
          Daftar kode produk yang tersedia untuk akun Anda. Gunakan <Text code>Product Code</Text> saat membuat transaksi via API.
        </Text>
      </div>

      {categories.length === 0 ? (
        <Empty description="Belum ada produk aktif. Hubungi admin Arendo untuk aktivasi." />
      ) : (
        <Row gutter={[16, 16]}>
          {categories.map(cat => (
            <Col xs={24} md={12} xl={8} key={cat.category_code}>
              <Card
                title={
                  <Space>
                    {categoryIcon(cat.category_code, cat.flow_type)}
                    <span>{cat.category_name}</span>
                    <Tag color={cat.flow_type === 'cash_in' ? 'blue' : 'red'} style={{ marginLeft: 4 }}>
                      {cat.flow_type === 'cash_in'
                        ? <><ArrowDownOutlined /> Cash In</>
                        : <><ArrowUpOutlined /> Cash Out</>}
                    </Tag>
                  </Space>
                }
                style={{ height: '100%' }}
                headStyle={{ borderBottom: '2px solid #0f3460' }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                  {cat.products.map(p => (
                    <div
                      key={p.product_code}
                      style={{
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        padding: '12px 14px'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <Text strong style={{ fontSize: 13 }}>{p.product_name}</Text>
                        <Badge status="success" text={<Text style={{ fontSize: 11, color: '#52c41a' }}>Aktif</Text>} />
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Product Code:</Text>
                        <Text code style={{ fontSize: 12, color: '#0f3460', fontWeight: 600 }}>
                          {p.product_code}
                        </Text>
                        <Tooltip title="Salin Product Code">
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => copy(p.product_code)}
                          />
                        </Tooltip>
                      </div>

                      <Space wrap size={[6, 4]}>
                        <Tag color="geekblue" style={{ fontSize: 11 }}>
                          Fee: {formatFee(p)}
                        </Tag>
                      </Space>

                      {p.description && (
                        <Paragraph
                          type="secondary"
                          style={{ fontSize: 11, marginBottom: 0, marginTop: 6 }}
                          ellipsis={{ rows: 2 }}
                        >
                          {p.description}
                        </Paragraph>
                      )}
                    </div>
                  ))}
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Card style={{ marginTop: 20, background: '#eff6ff', borderColor: '#bfdbfe' }}>
        <Space>
          <InfoCircleOutlined style={{ color: '#1d4ed8' }} />
          <Text style={{ color: '#1e40af', fontSize: 12 }}>
            Product Code digunakan di field <Text code>additionalInfo.productCode</Text> saat membuat transaksi via API.
            Lihat dokumentasi lengkap di <Text strong>ARENDO_API_QRIS_MITRA.pdf</Text>.
          </Text>
        </Space>
      </Card>
    </div>
  );
};

export default MerchantProducts;
